import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { forkJoin, Observable } from 'rxjs';
import { ERROR, SUCCESS } from 'src/app/global';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { User } from 'src/app/calvin-emr-shared/models/user.model';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { UserService } from 'src/app/calvin-emr-shared/services/user.service';
import { StudentsPageComponent } from '../students-page/students-page';
import { PATH_STUDENTS } from '../students.global';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { DividerModule } from 'primeng/divider';
import { ButtonDirective } from 'primeng/button';
import { Toast } from 'primeng/toast';


@Component({
  selector: 'app-assign-patients-page',
  templateUrl: './assign-patients-page.component.html',
  styleUrls: ['./assign-patients-page.component.scss'],
  standalone: true,
  imports: [
    BreadcrumbModule,
    TableModule,
    PrimeTemplate,
    DividerModule,
    ButtonDirective,
    Toast,
  ],
})
export class AssignPatientsPageComponent extends StudentsPageComponent implements OnInit {

  actRoute = inject(ActivatedRoute);

  breadcrumbItems: MenuItem[] = [
    { label: 'Student List', routerLink: `/${PATH_STUDENTS}` },
    { label: 'Assign Patients' },
  ];

  inputStudents: User[] = [];

  // Array of selected students from the student table
  selectedStudents: User[] = [];

  patients: Patient[] = [];
  selectedPatients: Patient[] = [];

  student2PatientNamesMap = new Map<string, string>();

  isSaving = false;
  isSaveDisabled = true;

  constructor(
    store: Store,
    userService: UserService,
    patientService: PatientService,
    private toastService: ToastService,
    private router: Router
  ) {
    super(store, userService, patientService);

    // Query params include an array of selected students, stringified.
    this.actRoute.queryParams.subscribe(params => {
      this.inputStudents = JSON.parse(params['data']);
    });

    this.startLoading();

    // Get the patients for each student. For the student's patients,
    // create a string that shows their names.
    this.inputStudents.map(stud => {
      this.patientService.getPatientsForUser(stud).subscribe(pat => {
        this.student2PatientNamesMap.set(stud.id,
          pat.map(p => `${p.firstName} ${p.lastName}`).join(', '));
      });
    });

    this.stopLoading();

    this.fetchStudents();   // all students
    this.fetchPatients();   // administrator's patients only
  }

  ngOnInit(): void {
  }

  /**
   * Fetches the current admin's patients to display in a table.
   */
  fetchPatients() {
    this.patientService.getPatientsForUser(this.currentUser).subscribe((patients) => {
      this.patients = patients;
    });
  }

  navigateToStudentsPage() {
    this.router.navigateByUrl(PATH_STUDENTS);
  }

  patientNamesForStudent(student: User): string {
    return this.student2PatientNamesMap.get(student.id) || 'Unknown';
  }

  /**
   * Saves the selected patient IDs to the selected student, and creates
   * new student patients if they do not already exist.
   * Currently overwrites currently existing student patients that are not assigned.
   */
  saveSelectedPatients() {
    this.isSaving = true;

    this.selectedStudents.forEach((student: User) => {

      let toAddObservables: Observable<any>[] = [];
      const toRemoveObservables: Observable<any>[] = [];
      const studentId = student.id;

      // Get student's current patients and new selection
      // Student's patient IDs are of the form [student ID]-[patient MRN]
      const oldPatientIds = student.patientIds;
      // const newPatientIds = this.selectedPatients
      //   .map((patient: Patient) => `${studentId}-${patient.mrn}`)
      //   .filter(newPatientId => !oldPatientIds.includes(newPatientId));

      // Patients to add are in selectedPatients and not in oldPatientIds
      const patientsToAdd = this.selectedPatients.filter(
        (newPatient) => !oldPatientIds.some((oldId) => oldId === `${studentId}-${newPatient.id}`)
      );
      const newPatientIds = patientsToAdd.map((patient: Patient) => `${studentId}-${patient.mrn}`);

      // Patients to remove are in oldPatientIds and not in selectedPatients
      // const patientIdsToRemove = oldPatientIds.filter(
      //   (oldId) => !newPatientIds.some((newId) => newId === oldId)
      // );
      // console.log('patientIdsToRemove = ', patientIdsToRemove);

      // Create student patients
      if (!!patientsToAdd.length) {
        // TODO: will need to change this in order to avoid overwriting currently existing student patients that aren't assigned
        toAddObservables = patientsToAdd.map((patient) =>
          this.patientService.createStudentPatient(patient, studentId)
        );
      }

      // TODO: decide if we should delete patients, or have a "reset" button
      // Delete student patients
      // if (!!patientIdsToRemove.length) {
      //   toRemoveObservables = patientIdsToRemove.map((patientId) =>
      //     this.patientService.deletePatient(patientId, COLLECTION_STUDENT_PATIENTS)
      //   );
      // }

      forkJoin([
        ...toAddObservables,
        // ...toRemoveObservables,
        this.userService.setPatientIdsForUser([...oldPatientIds, ...newPatientIds], studentId),
      ]).subscribe(
        () => {
          this.toastService.clearAndAddOne({
            severity: SUCCESS,
            summary: 'Success',
            detail: `Updated patients for ${student.firstNameLastName}!`,
          });
          this.navigateToStudentsPage();
        },
        (error) => {
          this.toastService.clearAndAddOne({
            severity: ERROR,
            summary: 'Error',
            detail: `Unable to update patients for ${student.firstNameLastName}: ${error}`,
          });
        },
        () => {
          this.isSaving = false;
          this.isSaveDisabled = true;
        }
      );
    }); // End of forEach loop for selected students
  }

}
